import { useAuth } from "@clerk/react";
import { Link } from "react-router-dom";
import {
  PublicFooter,
  PublicHeader,
  PublicPrimaryLink,
  PublicSecondaryLink,
  createBlueprintPath,
} from "@/components/PublicMarketingLayout";

const previewTabs = [
  "Overview",
  "Product Spec",
  "Data Model",
  "Acceptance Criteria",
  "Edge Cases",
  "Test Scenarios",
  "Build Prompt",
];

const deliverables = [
  "Plain-English product summary",
  "Product specification",
  "Screens and user experience",
  "App workflows",
  "Data model",
  "Acceptance criteria",
  "Edge cases",
  "Test scenarios",
  "Builder-ready implementation prompt",
  "Markdown and PDF export where included",
];

const audiences = [
  "Entrepreneurs",
  "Coaches and consultants",
  "Course creators",
  "Marketers",
  "Product owners",
  "Agencies",
  "Non-technical founders",
];

const pricingCards = [
  {
    name: "Free",
    description: "Start with one idea and see how the blueprint workflow feels.",
    bullets: ["1 blueprint per month", "Quick Mode", "Standard Build Prompt"],
    cta: "Start free",
  },
  {
    name: "Standard",
    description: "Plan more ideas with a clearer review step before generation.",
    bullets: [
      "5 blueprints per month",
      "Quick and Guided Mode",
      "Assumptions Review",
      "Standard Build Prompt",
    ],
    cta: "View Standard",
  },
  {
    name: "Pro",
    description: "Go deeper when the build needs stronger planning and QA.",
    bullets: [
      "25 blueprints per month",
      "Quick and Guided Mode",
      "Deep Build Prompt",
      "Detailed Product Spec and Data Model",
      "Acceptance Criteria, Edge Cases and Test Scenarios",
      "PDF export and Pro Tools",
    ],
    cta: "View Pro",
  },
];

export function LandingPage() {
  const { isLoaded, isSignedIn } = useAuth();
  const showAuthenticatedActions = isLoaded && isSignedIn;

  return (
    <div className="min-h-screen overflow-x-hidden bg-gradient-to-b from-white via-indigo-50/40 to-white text-slate-950">
      <PublicHeader />

      <main>
        <section className="mx-auto grid w-full max-w-7xl gap-10 px-4 py-16 sm:px-6 lg:grid-cols-[1.02fr_0.98fr] lg:px-8 lg:py-24">
          <div className="flex flex-col justify-center">
            <p className="w-fit rounded-full border border-indigo-200 bg-white px-4 py-1.5 text-sm font-semibold text-indigo-700 shadow-sm">
              No coding required. Start with one free blueprint.
            </p>
            <h1 className="mt-6 max-w-4xl text-4xl font-bold tracking-tight text-slate-950 sm:text-5xl lg:text-6xl">
              Turn your app idea into a build-ready blueprint
            </h1>
            <p className="mt-6 max-w-3xl text-base leading-8 text-slate-600 sm:text-lg">
              Describe your app idea in plain English. Get a clear product
              specification, screen plan, data model, acceptance criteria, edge
              cases, test scenarios and a builder-ready implementation prompt.
            </p>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <PublicPrimaryLink to={showAuthenticatedActions ? createBlueprintPath : "/sign-up"}>
                Try it free
              </PublicPrimaryLink>
              <a
                href="#how-it-works"
                className="inline-flex items-center justify-center rounded-xl border border-slate-200 bg-white px-5 py-3 text-sm font-semibold text-slate-800 shadow-sm transition hover:bg-slate-50 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
              >
                See how it works
              </a>
            </div>
          </div>

          <div className="rounded-[2rem] border border-indigo-100 bg-white/90 p-4 shadow-2xl shadow-indigo-100/70">
            <div className="rounded-[1.5rem] bg-gradient-to-br from-indigo-50 via-white to-violet-50 p-4 sm:p-5">
              <div className="flex items-center justify-between gap-3">
                <div>
                  <p className="text-xs font-semibold uppercase tracking-[0.18em] text-indigo-500">
                    Blueprint preview
                  </p>
                  <h2 className="mt-1 text-xl font-bold text-slate-950">
                    Client Portal App
                  </h2>
                </div>
                <span className="rounded-full bg-emerald-100 px-3 py-1 text-xs font-semibold text-emerald-700">
                  Build-ready
                </span>
              </div>

              <div className="mt-5 grid gap-2 sm:grid-cols-2">
                {previewTabs.map((tab) => (
                  <div
                    key={tab}
                    className="rounded-xl border border-white bg-white/80 px-3 py-2 text-sm font-semibold text-slate-700 shadow-sm"
                  >
                    {tab}
                  </div>
                ))}
              </div>

              <div className="mt-5 rounded-2xl border border-indigo-100 bg-white p-4">
                <p className="text-sm font-bold text-slate-950">
                  What the builder receives
                </p>
                <div className="mt-3 space-y-3">
                  {[
                    "Screens, fields, states and mobile behaviour",
                    "Data model with validation rules",
                    "Acceptance criteria and test scenarios",
                    "Step-by-step implementation prompt",
                  ].map((item) => (
                    <div key={item} className="flex gap-3 text-sm text-slate-600">
                      <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-indigo-500" />
                      <span>{item}</span>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className="border-y border-indigo-100 bg-white">
          <div className="mx-auto max-w-5xl px-4 py-16 text-center sm:px-6 lg:px-8">
            <h2 className="text-3xl font-bold tracking-tight text-slate-950">
              Stop giving AI builders vague instructions
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-base leading-8 text-slate-600">
              Unclear prompts lead to missing screens, conflicting workflows,
              repeated rebuilding and poor results. A blueprint gives the builder a
              practical plan before the first line of code is generated.
            </p>
            <div className="mx-auto mt-8 grid max-w-4xl gap-3 sm:grid-cols-3">
              {[
                "Missing screens and workflows",
                "Endless prompting and rebuilding",
                "Unclear scope and weak test coverage",
              ].map((painPoint) => (
                <div
                  key={painPoint}
                  className="rounded-2xl border border-indigo-100 bg-indigo-50/70 p-4 text-sm font-bold text-indigo-950"
                >
                  {painPoint}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section id="how-it-works" className="scroll-mt-24 px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-600">
                How it works
              </p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950">
                From rough idea to clear build plan
              </h2>
            </div>
            <div className="mt-8 grid gap-4 md:grid-cols-3">
              {[
                ["01", "Describe your idea", "Start with the plain-English version of what you want to build."],
                ["02", "Review the plan", "Check the assumptions, scope and structure before generation."],
                ["03", "Generate the blueprint", "Create a practical blueprint and AI build prompt for your next step."],
              ].map(([number, title, copy]) => (
                <article key={title} className="rounded-2xl border border-indigo-100 bg-white p-6 shadow-sm">
                  <span className="rounded-full bg-indigo-100 px-3 py-1 text-xs font-bold text-indigo-700">
                    {number}
                  </span>
                  <h3 className="mt-4 text-lg font-bold text-slate-950">{title}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{copy}</p>
                </article>
              ))}
            </div>
          </div>
        </section>

        <section id="features" className="scroll-mt-24 bg-indigo-950 px-4 py-16 text-white sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="max-w-3xl">
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-200">
                What you get
              </p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight">
                The planning pack your AI builder needs
              </h2>
            </div>
            <div className="mt-8 grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
              {deliverables.map((item) => (
                <div key={item} className="rounded-2xl border border-white/10 bg-white/10 p-4 text-sm font-semibold text-indigo-50">
                  {item}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto grid max-w-7xl gap-8 lg:grid-cols-[0.9fr_1.1fr]">
            <div>
              <p className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-600">
                Who it is for
              </p>
              <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950">
                Built for people with ideas, not technical teams
              </h2>
              <p className="mt-4 text-base leading-8 text-slate-600">
                You do not need to speak like a developer. Start with the outcome,
                the user and the workflow. The engine turns that into a structured
                plan.
              </p>
            </div>
            <div className="grid gap-3 sm:grid-cols-2">
              {audiences.map((audience) => (
                <div key={audience} className="rounded-2xl border border-slate-200 bg-white p-4 text-sm font-bold text-slate-800 shadow-sm">
                  {audience}
                </div>
              ))}
            </div>
          </div>
        </section>

        <section className="bg-slate-50 px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <h2 className="text-3xl font-bold tracking-tight text-slate-950">
              See the difference a blueprint makes
            </h2>
            <div className="mt-8 grid gap-5 lg:grid-cols-2">
              <article className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-slate-400">
                  Before
                </p>
                <p className="mt-4 text-xl font-semibold leading-8 text-slate-800">
                  "Build me an app where coaches can manage clients and session notes."
                </p>
              </article>
              <article className="rounded-3xl border border-indigo-100 bg-white p-6 shadow-sm">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-600">
                  After
                </p>
                <ul className="mt-4 grid gap-3 text-sm leading-6 text-slate-700">
                  {[
                    "Login and dashboard structure",
                    "Client profiles and session notes workflow",
                    "Data fields, validation and mobile behaviour",
                    "Acceptance criteria, edge cases and test scenarios",
                    "Build sequence for the AI builder to follow",
                  ].map((item) => (
                    <li key={item} className="flex gap-3">
                      <span className="mt-2 h-2 w-2 shrink-0 rounded-full bg-indigo-500" />
                      <span>{item}</span>
                    </li>
                  ))}
                </ul>
              </article>
            </div>
          </div>
        </section>

        <section className="px-4 py-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-7xl">
            <div className="flex flex-col gap-4 sm:flex-row sm:items-end sm:justify-between">
              <div className="max-w-3xl">
                <p className="text-sm font-semibold uppercase tracking-[0.18em] text-indigo-600">
                  Pricing preview
                </p>
                <h2 className="mt-3 text-3xl font-bold tracking-tight text-slate-950">
                  Start free. Upgrade when you need deeper planning.
                </h2>
              </div>
              <PublicSecondaryLink to="/pricing">View pricing</PublicSecondaryLink>
            </div>
            <div className="mt-8 grid gap-4 lg:grid-cols-3">
              {pricingCards.map((plan) => (
                <article key={plan.name} className="rounded-3xl border border-slate-200 bg-white p-6 shadow-sm">
                  <h3 className="text-xl font-bold text-slate-950">{plan.name}</h3>
                  <p className="mt-2 text-sm leading-6 text-slate-600">{plan.description}</p>
                  <ul className="mt-5 space-y-3 text-sm text-slate-700">
                    {plan.bullets.map((bullet) => (
                      <li key={bullet} className="flex gap-3">
                        <span className="mt-1 h-2 w-2 shrink-0 rounded-full bg-indigo-500" />
                        <span>{bullet}</span>
                      </li>
                    ))}
                  </ul>
                  <Link
                    to={plan.name === "Free" ? "/sign-up" : "/pricing"}
                    className="mt-6 inline-flex w-full items-center justify-center rounded-xl border border-indigo-200 bg-indigo-50 px-4 py-3 text-sm font-bold text-indigo-800 transition hover:bg-indigo-100 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2"
                  >
                    {plan.cta}
                  </Link>
                </article>
              ))}
            </div>
            <div className="mt-8 flex flex-col gap-3 sm:flex-row">
              <PublicPrimaryLink to="/sign-up">Start free</PublicPrimaryLink>
              <PublicSecondaryLink to="/pricing">View pricing</PublicSecondaryLink>
            </div>
          </div>
        </section>

        <section className="px-4 pb-16 sm:px-6 lg:px-8">
          <div className="mx-auto max-w-5xl rounded-[2rem] border border-indigo-100 bg-gradient-to-br from-indigo-600 to-violet-600 p-8 text-center text-white shadow-xl shadow-indigo-100 sm:p-12">
            <h2 className="text-3xl font-bold tracking-tight sm:text-4xl">
              Your next app should start with a blueprint, not a guess
            </h2>
            <p className="mx-auto mt-4 max-w-3xl text-base leading-8 text-indigo-50">
              Turn your idea into a clear, build-ready plan before you spend hours
              prompting, rebuilding and fixing avoidable mistakes.
            </p>
            <div className="mt-8 flex flex-col justify-center gap-3 sm:flex-row">
              <Link
                to={showAuthenticatedActions ? createBlueprintPath : "/sign-up"}
                className="inline-flex items-center justify-center rounded-xl bg-white px-5 py-3 text-sm font-bold text-indigo-700 shadow-sm transition hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600"
              >
                Create your free blueprint
              </Link>
              <Link
                to="/sign-in"
                className="inline-flex items-center justify-center rounded-xl border border-white/40 px-5 py-3 text-sm font-bold text-white transition hover:bg-white/10 focus:outline-none focus:ring-2 focus:ring-white focus:ring-offset-2 focus:ring-offset-indigo-600"
              >
                Log in
              </Link>
            </div>
          </div>
        </section>
      </main>

      <PublicFooter />
    </div>
  );
}
